/** @jsxImportSource @emotion/react */
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React, {
  Fragment,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Edit, Trash2, X as CloseIcon } from 'react-feather';
import {
  allDayOptions,
  dayOptions,
  everyDayIndex,
  scheduleFormDefaultValue,
} from '../../constants';
import { ScheduleBaseModel, ScheduleDefaultModel } from '../../models';
import { useCustomFormik } from '../../utils';
import { MainLoader } from '../main-loader';
import { ScheduleFormConfig, ScheduleFormType } from './ScheduleForm.const';
import { offerScheduleSchema } from './ScheduleForm.schema';
import { useOfferScheduleFormStyles } from './ScheduleForm.styles';
import { OfferScheduleFormModel } from './ScheduleForm.types';
import { mapEditableSchedule } from './utils/mapEditableSchedule';
import { useDeleteOfferGeoSchedules } from './utils/useDeleteOfferGeoSchedules';
import { useGetOfferGeoSchedules } from './utils/useGetOfferGeoSchedules';
import { useSaveOfferGeoSchedules } from './utils/useSaveOfferGeoSchedules';

interface OfferScheduleFormProps {
  parentId: string;
  isBlocked: boolean;
  type?: ScheduleFormType;
  onHasSchedule?: (has: boolean) => void;
}

export function ScheduleForm({
  isBlocked,
  parentId,
  type = ScheduleFormType.Offer,
  onHasSchedule = () => null,
  children,
}: PropsWithChildren<OfferScheduleFormProps>): JSX.Element {
  const style = useOfferScheduleFormStyles();
  const config = ScheduleFormConfig[type];

  if (!config) {
    return <div>Wrong type!</div>;
  }

  const [editableId, setEditableId] = useState<string>();

  const [{ data: schedules, loading: scheduleLoading }, update] =
    useGetOfferGeoSchedules(config(parentId));
  const [{ loading: saveLoading, error }, saveFormData] =
    useSaveOfferGeoSchedules(config(parentId));
  const [{ loading: deleteLoading }, remove] =
    useDeleteOfferGeoSchedules(config);

  const loading = scheduleLoading || saveLoading || deleteLoading;

  useEffect(() => {
    onHasSchedule(!!schedules.length);
  }, [schedules]);

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    isValid,
    dirty,
    handleBlur,
    setFieldValue,
  } = useCustomFormik<OfferScheduleFormModel>(
    {
      skipErrorKey: 'schedules',
      validationSchema: offerScheduleSchema,
      initialValues: { schedules: [] },
      validateOnChange: false,
      validateOnBlur: true,
      onSubmit: (formValues) => {
        const value = mapEditableSchedule(formValues.schedules, editableId);

        saveFormData(value, !!editableId).then(() => {
          update();

          setEditableId(null);
          setFieldValue('schedules', []);
        });
      },
    },
    error,
  );

  const deleteRow = (rowIndex) => {
    setFieldValue(
      'schedules',
      values.schedules.filter((item, index) => index !== rowIndex),
    );
  };

  const removeTimeSlot = (slotId: string) => {
    remove(slotId).then(() => {
      update();
    });
  };

  const removeTimeSlots = (slots: ScheduleDefaultModel[]) => {
    if (!slots.length) {
      update();
      return;
    }

    remove(slots[0].id).then(() => {
      removeTimeSlots(slots.slice(1));
    });
  };

  const renderFormRow = (
    { day, timeTo, timeFrom }: ScheduleBaseModel,
    index: number,
  ): JSX.Element => {
    const disabled = !!editableId && day === everyDayIndex;
    const options = disabled || !editableId ? allDayOptions : dayOptions;

    return (
      <Grid className="schedule-row" key={index} container spacing={1}>
        <Grid item xs={12} sm={6} md={4} xl={4}>
          <FormControl
            className="text-field"
            variant="outlined"
            disabled={disabled}
            fullWidth
            error={touched && !!errors[`${index}.day`]}
          >
            <InputLabel id={`day_${index}`}>Day</InputLabel>
            <Select
              labelId={`day_${index}`}
              value={day}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
              name={`schedules.${index}.day`}
              label="Day"
            >
              {options.map((item, dayIndex) => (
                <MenuItem
                  className="MuiAutocomplete-option"
                  key={dayIndex}
                  value={dayIndex}
                >
                  {item}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{errors[`${index}.day`] || ''}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3} md={4} xl={4}>
          <TextField
            fullWidth
            error={touched && !!errors[`${index}.timeFrom`]}
            helperText={errors[`${index}.timeFrom`]}
            label="Time From"
            name={`schedules.${index}.timeFrom`}
            onChange={handleChange}
            onBlur={handleBlur}
            value={timeFrom || ''}
            className="text-field"
            type="time"
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              step: 300, // 5 min
            }}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={4} xl={4}>
          <TextField
            fullWidth
            error={touched && !!errors[`${index}.timeTo`]}
            helperText={errors[`${index}.timeTo`]}
            label="Time To"
            name={`schedules.${index}.timeTo`}
            onChange={handleChange}
            onBlur={handleBlur}
            value={timeTo || ''}
            className="text-field"
            type="time"
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              step: 300, // 5 min
            }}
          />
        </Grid>
        <div className="schedule-delete-btn">
          <IconButton
            color="secondary"
            data-test-id={`schedule-f-delete-button-${index}`}
            aria-label="Delete"
            onClick={() => {
              setEditableId(null);
              deleteRow(index);
            }}
            size="large"
          >
            {editableId ? <CloseIcon size={20} /> : <Trash2 size={20} />}
          </IconButton>
        </div>
      </Grid>
    );
  };

  const renderListRow = (
    { timeFrom, timeTo, day, id, ids }: ScheduleDefaultModel,
    index,
    dayTitle: string,
  ) => {
    if (editableId === id && values.schedules[0]) {
      return renderFormRow(values.schedules[0], 0);
    }

    return (
      <Grid className="schedule-row" key={id} container spacing={1}>
        <Grid item xs={6} sm={6} md={6} xl={6}>
          {index === 0 ? dayTitle : ''}
        </Grid>
        <Grid item xs={6} sm={6} md={6} xl={6}>
          <Box
            mb={1}
            display="flex"
            alignItems="center"
            key={`${timeFrom}${timeTo}`}
          >
            <Box mr={1}>
              {timeFrom} - {timeTo}
            </Box>
            {!values.schedules.length && (
              <IconButton
                size="small"
                color="primary"
                data-test-id={`schedule-f-delete-button-${index}`}
                aria-label="Delete"
                onClick={() => {
                  setFieldValue('schedules', [{ timeFrom, timeTo, day, ids }]);
                  setEditableId(id);
                }}
                disabled={loading}
              >
                <Edit size={15} />
              </IconButton>
            )}
            <IconButton
              size="small"
              color="secondary"
              data-test-id={`schedule-f-delete-button-${index}`}
              aria-label="Delete"
              onClick={() => (ids ? removeTimeSlots(ids) : removeTimeSlot(id))}
              disabled={loading}
            >
              <CloseIcon size={15} />
            </IconButton>
          </Box>
        </Grid>
      </Grid>
    );
  };

  const list = useMemo(
    () =>
      schedules.map(({ day, schedule, isEveryDay }) => (
        <Box pt={1} pb={1} key={day}>
          {isEveryDay
            ? renderListRow(
                { ...schedule[0], day: everyDayIndex, ids: schedule },
                0,
                'Every day',
              )
            : schedule.map((scheduleItem, index) =>
                renderListRow(scheduleItem, index, day),
              )}
        </Box>
      )),
    [schedules, removeTimeSlot, loading],
  );

  const footer = (
    <Fragment>
      <Divider />
      <Box display="flex" justifyContent="space-between" p={2}>
        <Button
          color="primary"
          type="button"
          variant="contained"
          disabled={isBlocked || loading || !!editableId}
          onClick={() =>
            setFieldValue('schedules', [
              ...values.schedules,
              { ...scheduleFormDefaultValue },
            ])
          }
        >
          Add schedule
        </Button>
        {(!!values.schedules.length || editableId) && (
          <Button
            color="primary"
            type="submit"
            variant="contained"
            disabled={isBlocked || !isValid || !dirty || loading}
          >
            Save
          </Button>
        )}
      </Box>
    </Fragment>
  );

  return (
    <form css={style} onSubmit={handleSubmit}>
      {loading && <MainLoader fillContainer />}

      <Card>
        <CardHeader title="Offer Geo schedule" />
        {!!schedules.length && (
          <Fragment>
            <Divider />
            <CardContent>{list}</CardContent>
          </Fragment>
        )}
        {!editableId && !!values.schedules.length && (
          <Fragment>
            <Divider />
            <CardContent>
              <div>{values.schedules.map(renderFormRow)}</div>
            </CardContent>
          </Fragment>
        )}
        {footer}
        <Divider />
        <Box p={2} display="flex" justifyContent="flex-end">
          {children && <Box>{children}</Box>}
        </Box>
      </Card>
    </form>
  );
}
